class ProlockeyTheme {
  constructor() {
    this.lastTrigger = null;
    this.gaMeasurementId = 'G-QK59XFDCV4';
    this.gaReady = false;
    this.gaConsentDenied = false;
    this.pendingGaEvents = [];
    this.gaEventAllowlist = new Set(['generate_lead', 'whatsapp_lead', 'email_lead', 'catalog_download']);
    this.bindHeaderScroll();
    this.bindDrawers();
    this.bindMegaMenus();
    this.bindTabs();
    this.bindRfq();
    this.bindContactDock();
    this.bindPredictiveSearch();
    this.bindProductForms();
    this.bindCartMoqValidation();
    this.bindReveal();
    this.bindHeroScene();
    this.bindLeadershipCarousels();
    this.bindAnalytics();
    this.initLeadAnalytics();
    document.documentElement.classList.remove('no-js');
  }

  bindHeaderScroll() {
    const header = document.querySelector('[data-global-header]');
    if (!header) return;

    const headerGroup = header.closest('.shopify-section-group-header-group');
    let ticking = false;
    const update = () => {
      const scrolled = window.scrollY > 24;
      header.classList.toggle('is-scrolled', scrolled);
      headerGroup?.classList.toggle('is-header-scrolled', scrolled);
      ticking = false;
    };

    update();
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });
  }

  toggleLayer(layer, open, trigger) {
    if (!layer) return;
    layer.classList.toggle('is-open', open);
    layer.setAttribute('aria-hidden', open ? 'false' : 'true');
    document.body.classList.toggle('is-locked', open);
    if (open) {
      this.lastTrigger = trigger || document.activeElement;
      requestAnimationFrame(() => layer.querySelector('button, [href], input, select, textarea')?.focus());
    } else if (this.lastTrigger) {
      this.lastTrigger.focus();
    }
  }

  bindDrawers() {
    document.querySelectorAll('[data-open-layer]').forEach((button) => {
      button.addEventListener('click', () => this.toggleLayer(document.getElementById(button.dataset.openLayer), true, button));
    });
    document.querySelectorAll('[data-close-layer]').forEach((button) => {
      button.addEventListener('click', () => this.toggleLayer(button.closest('.mobile-drawer, .search-panel'), false));
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('.is-open.mobile-drawer, .is-open.search-panel').forEach((layer) => this.toggleLayer(layer, false));
      document.querySelectorAll('.mega-menu.is-open').forEach((menu) => menu.classList.remove('is-open'));
      document.querySelectorAll('[data-mega-trigger][aria-expanded="true"]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    });
  }

  bindMegaMenus() {
    const closeMenus = () => {
      document.querySelectorAll('.mega-menu.is-open').forEach((menu) => menu.classList.remove('is-open'));
      document.querySelectorAll('[data-mega-trigger][aria-expanded="true"]').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
    };

    document.addEventListener('pointerdown', (event) => {
      if (!event.target.closest('.site-header')) closeMenus();
    });

    document.querySelectorAll('[data-mega-trigger]').forEach((trigger) => {
      const menu = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!menu) return;

      const openMenu = () => {
        closeMenus();
        menu.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      };

      if (trigger.matches('a[href]')) {
        trigger.addEventListener('mouseenter', openMenu);
        trigger.addEventListener('focus', openMenu);
        menu.addEventListener('mouseenter', openMenu);
        trigger.closest('.site-header')?.addEventListener('mouseleave', closeMenus);
      } else {
        trigger.addEventListener('click', () => {
          const opening = !menu.classList.contains('is-open');
          closeMenus();
          if (opening) openMenu();
        });
      }
    });
  }

  bindTabs() {
    document.querySelectorAll('[data-tabs]').forEach((group) => {
      const tabs = [...group.querySelectorAll('[role="tab"]')];
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.activateTab(group, tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          const next = event.key === 'ArrowRight' ? (index + 1) % tabs.length : (index - 1 + tabs.length) % tabs.length;
          tabs[next].focus();
          this.activateTab(group, tabs[next]);
        });
      });
    });
  }

  activateTab(group, selected) {
    group.querySelectorAll('[role="tab"]').forEach((tab) => {
      const active = tab === selected;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    group.querySelectorAll('[role="tabpanel"]').forEach((panel) => panel.classList.toggle('is-active', panel.id === selected.getAttribute('aria-controls')));
  }

  bindRfq() {
    const dialog = document.getElementById('RfqDialog');
    if (!dialog) return;

    const form = dialog.querySelector('form');
    const pendingSubmissionKey = 'prolockey:rfq-submission';
    form?.addEventListener('submit', () => {
      try {
        sessionStorage.setItem(pendingSubmissionKey, String(Date.now()));
      } catch (error) {
        // Session storage can be unavailable in privacy-restricted browsers.
      }
    });

    document.querySelectorAll('[data-open-rfq]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.lastTrigger = button;
        if (form) {
          ['product', 'sku', 'variant', 'quantity', 'url', 'purchaseMode'].forEach((key) => {
            const input = form.querySelector(`[data-rfq-${key}]`);
            if (input) input.value = button.dataset[key] || '';
          });
        }
        dialog.showModal();
        dialog.querySelector('input:not([type="hidden"])')?.focus();
        this.track('rfq_start', { product: button.dataset.product || '', sku: button.dataset.sku || '', purchase_mode: button.dataset.purchaseMode || '' });
      });
    });
    dialog.querySelector('[data-close-dialog]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
    dialog.addEventListener('close', () => this.lastTrigger?.focus());

    const feedback = dialog.querySelector('.form-status, .errors');
    const success = dialog.querySelector('.form-status');

    if (success) {
      const successKey = `prolockey:rfq-success:${window.location.pathname}${window.location.search}`;
      let shouldTrack = success.dataset.analyticsTracked !== 'true';
      let submissionId = '';

      try {
        submissionId = sessionStorage.getItem(pendingSubmissionKey) || '';
        const lastTrackedSubmission = sessionStorage.getItem(successKey) || '';
        shouldTrack = submissionId ? lastTrackedSubmission !== submissionId : !lastTrackedSubmission;

        if (shouldTrack) {
          sessionStorage.setItem(successKey, submissionId || 'rendered');
        }
        sessionStorage.removeItem(pendingSubmissionKey);
      } catch (error) {
        // Fall back to the DOM marker when storage is unavailable.
      }

      if (shouldTrack) {
        success.dataset.analyticsTracked = 'true';
        this.track('generate_lead', {
          form_id: form?.id || 'RfqForm',
          form_name: 'request_quote',
          lead_source: 'rfq_form'
        });
      }
    }

    if (feedback && !dialog.open) {
      dialog.showModal();
      requestAnimationFrame(() => feedback.focus?.());
    }
  }

  bindContactDock() {
    const dock = document.querySelector('[data-contact-dock]');
    if (!dock) return;
    dock.querySelector('[data-contact-toggle]')?.addEventListener('click', () => {
      const open = dock.classList.toggle('is-open');
      dock.querySelector('[data-contact-toggle]').setAttribute('aria-expanded', String(open));
    });
  }

  bindPredictiveSearch() {
    const input = document.querySelector('[data-predictive-input]');
    const target = document.querySelector('[data-predictive-results]');
    if (!input || !target) return;
    let timer;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const query = input.value.trim();
      if (query.length < 2) { target.innerHTML = ''; return; }
      timer = setTimeout(async () => {
        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}search/suggest?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article&section_id=predictive-search`);
          const markup = await response.text();
          const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
          target.innerHTML = documentFragment.querySelector('#shopify-section-predictive-search')?.innerHTML || '';
          this.track('product_search', { search_term: query });
        } catch (error) {
          target.innerHTML = '';
        }
      }, 220);
    });
  }

  bindProductForms() {
    document.querySelectorAll('[data-product-root]').forEach((root) => {
      const select = root.querySelector('[data-variant-select]');
      if (!select) return;
      const variants = JSON.parse(root.querySelector('[data-product-variants]')?.textContent || '[]');
      select.addEventListener('change', () => {
        const variant = variants.find((item) => String(item.id) === select.value);
        if (!variant) return;
        const price = root.querySelector('[data-product-price]');
        const sku = root.querySelector('[data-product-sku]');
        const add = root.querySelector('[name="add"]');
        if (price) price.textContent = this.formatMoney(variant.price, root.dataset.currencyFormat || '${{amount}}');
        if (sku) sku.textContent = variant.sku || '—';
        if (add) { add.disabled = !variant.available; add.textContent = variant.available ? add.dataset.availableText : add.dataset.soldText; }
        const url = new URL(window.location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url);
        root.querySelectorAll('[data-open-rfq]').forEach((button) => { button.dataset.sku = variant.sku || ''; button.dataset.variant = variant.title || ''; });
        this.track('select_variant', { product_id: root.dataset.productId, variant_id: variant.id, sku: variant.sku });
      });
    });
  }

  bindCartMoqValidation() {
    const form = document.querySelector('[data-cart-form]');
    if (!form) return;

    const quantityInputs = [...form.querySelectorAll('[data-cart-quantity]')];
    const checkoutButton = form.querySelector('[data-cart-checkout]');
    const status = document.querySelector('[data-cart-moq-status]');

    const validate = () => {
      let firstInvalid = null;

      quantityInputs.forEach((input) => {
        const minimum = Number(input.dataset.moq || input.min || 1);
        const quantity = Number(input.value);
        const invalid = !Number.isFinite(quantity) || quantity < minimum;
        const message = form.querySelector(`#${input.getAttribute('aria-describedby')}`);

        input.setAttribute('aria-invalid', String(invalid));
        input.setCustomValidity(invalid ? `Minimum order quantity is ${minimum} units.` : '');
        if (message) message.hidden = !invalid;
        if (invalid && !firstInvalid) firstInvalid = input;
      });

      if (status) status.hidden = !firstInvalid;
      if (checkoutButton) {
        checkoutButton.disabled = Boolean(firstInvalid);
        checkoutButton.setAttribute('aria-disabled', String(Boolean(firstInvalid)));
      }

      return firstInvalid;
    };

    quantityInputs.forEach((input) => {
      input.addEventListener('input', validate);
      input.addEventListener('change', validate);
    });

    form.addEventListener('submit', (event) => {
      const firstInvalid = validate();
      if (!firstInvalid) return;

      event.preventDefault();
      firstInvalid.reportValidity();
      firstInvalid.focus();
      status?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center'
      });
    });

    validate();
  }

  formatMoney(cents, format) {
    const amount = (Number(cents) / 100).toFixed(2);
    return format.replace('{{amount}}', amount).replace('{{ amount }}', amount);
  }

  bindReveal() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (entry.isIntersecting) { entry.target.classList.add('is-visible'); observer.unobserve(entry.target); }
    }), { threshold: .12 });
    document.querySelectorAll('[data-reveal]').forEach((item) => observer.observe(item));
  }

  bindHeroScene() {
    const hero = document.querySelector('[data-hero-scene]');
    const media = hero?.querySelector('[data-hero-media]');
    const canMove = window.matchMedia('(pointer: fine)').matches && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!hero || !media || !canMove) return;

    hero.addEventListener('pointermove', (event) => {
      const bounds = hero.getBoundingClientRect();
      const x = ((event.clientX - bounds.left) / bounds.width - .5) * -7;
      const y = ((event.clientY - bounds.top) / bounds.height - .5) * -5;
      media.style.setProperty('--hero-x', `${x}px`);
      media.style.setProperty('--hero-y', `${y}px`);
    }, { passive: true });

    hero.addEventListener('pointerleave', () => {
      media.style.setProperty('--hero-x', '0px');
      media.style.setProperty('--hero-y', '0px');
    });
  }

  bindLeadershipCarousels() {
    document.querySelectorAll('[data-leadership-carousel]').forEach((carousel) => {
      const viewport = carousel.querySelector('[data-leadership-viewport]');
      const slides = [...carousel.querySelectorAll('[data-leadership-slide]')];
      const dots = [...carousel.querySelectorAll('[data-leadership-dot]')];
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!viewport || slides.length === 0) return;

      let activeIndex = 0;

      const setActive = (index, shouldScroll = true) => {
        activeIndex = (index + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === activeIndex));
        dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', dotIndex === activeIndex ? 'true' : 'false'));

        if (shouldScroll) {
          const slide = slides[activeIndex];
          const left = slide.offsetLeft - (viewport.clientWidth - slide.offsetWidth) / 2;
          viewport.scrollTo({ left, behavior: reducedMotion ? 'auto' : 'smooth' });
        }
      };

      carousel.querySelector('[data-leadership-prev]')?.addEventListener('click', () => setActive(activeIndex - 1));
      carousel.querySelector('[data-leadership-next]')?.addEventListener('click', () => setActive(activeIndex + 1));
      dots.forEach((dot) => dot.addEventListener('click', () => setActive(Number(dot.dataset.index))));

      viewport.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        setActive(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
      });

      let scrollTimer;
      viewport.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          const viewportCenter = viewport.scrollLeft + viewport.clientWidth / 2;
          const closestIndex = slides.reduce((bestIndex, slide, index) => {
            const center = slide.offsetLeft + slide.offsetWidth / 2;
            const bestCenter = slides[bestIndex].offsetLeft + slides[bestIndex].offsetWidth / 2;
            return Math.abs(center - viewportCenter) < Math.abs(bestCenter - viewportCenter) ? index : bestIndex;
          }, 0);
          if (closestIndex !== activeIndex) setActive(closestIndex, false);
        }, 80);
      }, { passive: true });

      setActive(activeIndex, false);
      requestAnimationFrame(() => setActive(activeIndex));
    });
  }

  initLeadAnalytics() {
    const enableGoogleAnalytics = () => {
      const customerPrivacy = window.Shopify?.customerPrivacy;
      const analyticsAllowed = typeof customerPrivacy?.analyticsProcessingAllowed === 'function'
        ? customerPrivacy.analyticsProcessingAllowed()
        : true;

      if (!analyticsAllowed) {
        this.gaConsentDenied = true;
        window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
        return;
      }

      this.gaConsentDenied = false;
      window.gtag?.('consent', 'update', { analytics_storage: 'granted' });
      if (this.gaReady) return;

      window.dataLayer = window.dataLayer || [];
      window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
      window.gtag('js', new Date());
      window.gtag('config', this.gaMeasurementId, { send_page_view: false });

      if (!document.querySelector(`script[data-prolockey-ga4="${this.gaMeasurementId}"]`)) {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(this.gaMeasurementId)}`;
        script.dataset.prolockeyGa4 = this.gaMeasurementId;
        document.head.appendChild(script);
      }

      this.gaReady = true;
      this.pendingGaEvents.splice(0).forEach(([eventName, payload]) => {
        window.gtag('event', eventName, payload);
      });
    };

    if (window.Shopify?.customerPrivacy) {
      enableGoogleAnalytics();
    } else if (window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        (error) => { if (!error) enableGoogleAnalytics(); }
      );
    } else {
      enableGoogleAnalytics();
    }

    document.addEventListener('visitorConsentCollected', enableGoogleAnalytics);
  }

  bindAnalytics() {
    document.addEventListener('click', (event) => {
      const trackedTarget = event.target.closest('[data-event]');
      const link = event.target.closest('a[href]');
      if (!trackedTarget && !link) return;

      const rawHref = link?.getAttribute('href') || '';
      const fileName = rawHref.split(/[?#]/)[0].split('/').pop() || '';
      let eventName = trackedTarget?.dataset.event || '';
      let leadSource = '';

      if (/^(?:https?:)?\/\/(?:api\.)?whatsapp\.com\//i.test(rawHref) || /^(?:https?:)?\/\/wa\.me\//i.test(rawHref)) {
        eventName = 'whatsapp_lead';
        leadSource = 'whatsapp';
      } else if (/^mailto:/i.test(rawHref)) {
        eventName = 'email_lead';
        leadSource = 'email';
      } else if (/\.(?:pdf|docx?|xlsx?|pptx?|zip)$/i.test(fileName)) {
        eventName = 'catalog_download';
        leadSource = 'resource_download';
      }

      if (!eventName) return;

      this.track(eventName, {
        product_id: trackedTarget?.dataset.productId || '',
        sku: trackedTarget?.dataset.sku || '',
        purchase_mode: trackedTarget?.dataset.purchaseMode || '',
        lead_source: leadSource,
        file_name: eventName === 'catalog_download' ? fileName : ''
      });
    });
  }

  track(eventName, payload = {}) {
    const eventPayload = { ...payload, page_location: window.location.href };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...eventPayload });

    if (window.Shopify?.analytics?.publish) {
      const publishedEvent = window.Shopify.analytics.publish(`prolockey:${eventName}`, eventPayload);
      publishedEvent?.catch?.(() => {});
    }

    if (!this.gaEventAllowlist.has(eventName) || this.gaConsentDenied) return;

    if (!this.gaReady || !window.gtag) {
      this.pendingGaEvents.push([eventName, eventPayload]);
      return;
    }

    window.gtag('event', eventName, eventPayload);
  }
}

document.addEventListener('DOMContentLoaded', () => new ProlockeyTheme());
